import { z } from 'zod';

export const GovernanceSourceSchema = z.enum([
  'provider_listing', 'verified_documentation', 'application_policy', 'observation', 'estimate',
]);
export const GovernedFactSchema = z.object({
  value: z.union([z.string(), z.number(), z.boolean(), z.array(z.string())]).nullable(),
  unit: z.enum(['tokens', 'seconds', 'pages', 'images', 'dimensions', 'USD_per_million_tokens', 'boolean', 'identifier']),
  scope: z.enum(['provider_request', 'application_request', 'application_job', 'storage']),
  source: GovernanceSourceSchema,
  evidence: z.string().max(2048),
  verifiedAt: z.string().datetime().nullable(),
  status: z.enum(['known', 'unknown', 'stale', 'conflict']),
  revision: z.number().int().nonnegative(),
}).strict();
export type GovernedFact = z.infer<typeof GovernedFactSchema>;
const numericFact = <U extends GovernedFact['unit'], S extends GovernedFact['scope'] = 'provider_request'>(
  unit: U, scope: S = 'provider_request' as S,
) => GovernedFactSchema.extend({
  value: z.number().nonnegative().nullable(),
  unit: z.literal(unit),
  scope: z.literal(scope),
});

export const ModelGovernanceSchema = z.object({
  schemaVersion: z.literal(1),
  inputTokens: numericFact('tokens').optional(),
  imagesPerRequest: numericFact('images').optional(),
  nativePdfPagesPerRequest: numericFact('pages').optional(),
  audioSecondsPerRequest: numericFact('seconds').optional(),
  videoSecondsPerRequest: numericFact('seconds').optional(),
  storedDimensions: numericFact('dimensions', 'storage').optional(),
  nativeDimensions: numericFact('dimensions').optional(),
  minimumOutputDimensions: numericFact('dimensions').optional(),
  maximumOutputDimensions: numericFact('dimensions').optional(),
  applicationVideoSeconds: numericFact('seconds', 'application_request').optional(),
  applicationPdfPages: numericFact('pages', 'application_job').optional(),
  inputModalities: GovernedFactSchema.extend({
    value: z.array(z.enum(['text', 'image', 'audio', 'video', 'pdf'])).nullable(),
    unit: z.literal('identifier'),
    scope: z.literal('provider_request'),
  }).optional(),
  textPrice: numericFact('USD_per_million_tokens').optional(),
  imagePrice: numericFact('USD_per_million_tokens').optional(),
  audioPrice: numericFact('USD_per_million_tokens').optional(),
  videoPrice: numericFact('USD_per_million_tokens').optional(),
}).strict().superRefine((governance, ctx) => {
  for (const [key, fact] of Object.entries(governance)) {
    if (typeof fact === 'number' || fact === undefined) continue;
    if ((fact.status === 'unknown' && fact.value !== null) || (fact.status === 'known' && fact.value === null)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [key, 'value'], message: 'unknown requires null; known requires a value' });
    }
  }
});
export type ModelGovernance = z.infer<typeof ModelGovernanceSchema>;

// Strict allowlist: never persist prompts, credentials, arbitrary metadata or media.
export const GovernanceSnapshotSchema = z.object({
  deleted: z.boolean().optional(),
  provider: z.string().nullable().optional(),
  model_id: z.string().nullable().optional(),
  endpoint_id: z.string().nullable().optional(),
  capability: z.string().nullable().optional(),
  modality: z.array(z.string()).nullable().optional(),
  features: z.array(z.string()).nullable().optional(),
  context_window: z.number().nullable().optional(),
  max_output_tokens: z.number().nullable().optional(),
  deprecated: z.boolean().nullable().optional(),
  preview: z.boolean().nullable().optional(),
  missed_discoveries: z.number().nullable().optional(),
  source: z.string().nullable().optional(),
  pricing: z.object({
    input_per_1k: z.number().nullable().optional(),
    output_per_1k: z.number().nullable().optional(),
    cached_input_per_1k: z.number().nullable().optional(),
    currency: z.string().nullable().optional(),
    unit: z.string().nullable().optional(),
  }).strict().nullable().optional(),
  embedder_provider: z.string().nullable().optional(),
  embedder_model: z.string().nullable().optional(),
  embedder_image_chunks_enabled: z.boolean().nullable().optional(),
  gemini_chat_model: z.string().nullable().optional(),
  openai_chat_model: z.string().nullable().optional(),
  wiki_scrape_index_media: z.boolean().nullable().optional(),
  gemini_daily_quota_usd: z.union([z.string().regex(/^-?\d+(\.\d+)?$/), z.number()]).nullable().optional(),
  service_key: z.string().nullable().optional(),
  model: z.string().nullable().optional(),
  current_provider: z.string().nullable().optional(),
  current_model: z.string().nullable().optional(),
  enabled: z.boolean().nullable().optional(),
  settings: z.object({
    temperature: z.number().nullable().optional(),
    max_tokens: z.number().nullable().optional(),
    maxTokens: z.number().nullable().optional(),
    timeout_max_ms: z.number().nullable().optional(),
    enabled: z.boolean().nullable().optional(),
    thinking_budget: z.number().nullable().optional(),
    thinking_level: z.string().nullable().optional(),
    top_p: z.number().nullable().optional(),
    top_k: z.number().nullable().optional(),
    query_expansion: z.boolean().nullable().optional(),
    hyde: z.boolean().nullable().optional(),
    parent_document: z.boolean().nullable().optional(),
    adaptive_topk: z.boolean().nullable().optional(),
    rag_plus_initialized: z.boolean().nullable().optional(),
    rerank_multimodal_boost: z.number().nullable().optional(),
  }).strict().optional(),
  governance: ModelGovernanceSchema.optional(),
  listingConflict: z.object({
    status: z.literal('conflict'),
    source: z.literal('provider_listing'),
    context_window: z.number().nullable(),
    max_output_tokens: z.number().nullable(),
    modality: z.array(z.string()),
  }).strict().optional(),
}).strict();
export const GovernanceRevisionSchema = z.object({
  subject: z.string(),
  tenant_id: z.string().uuid().nullable(),
  revision: z.coerce.number().int().positive(),
  previous_revision: z.coerce.number().int().nonnegative(),
  schema_version: z.literal(1),
  snapshot: GovernanceSnapshotSchema,
  actor: z.string(),
  reason: z.string(),
  created_at: z.string(),
}).strict();
export type GovernanceRevision = z.infer<typeof GovernanceRevisionSchema>;
export type GovernanceSnapshot = z.infer<typeof GovernanceSnapshotSchema>;
export const GovernanceHistorySchema = z.array(GovernanceRevisionSchema);

export const CatalogRowDtoSchema = z.object({
  provider: z.string(),
  model_id: z.string(),
  capability: z.string(),
  display_name: z.string().nullable(),
  modality: z.array(z.string()),
  context_window: z.number().nullable(),
  preview: z.boolean(),
  pricing: z.record(z.unknown()).nullable(),
  features: z.array(z.string()),
  lifecycle_status: z.enum(['active', 'deprecating', 'unavailable']).optional(),
  deprecated: z.boolean(),
  is_recommended_for: z.array(z.string()).optional(),
  governance_revision: z.coerce.number().int().nonnegative().optional(),
  source: z.string().optional(),
  metadata: z.object({
    governance: ModelGovernanceSchema.optional(),
    latest_listing: z.object({
      context_window: z.number().nullable().optional(),
      max_output_tokens: z.number().nullable().optional(),
      modality: z.array(z.string()).optional(),
    }).passthrough().optional(),
  }).passthrough().optional(),
});
export type CatalogRowDto = z.infer<typeof CatalogRowDtoSchema>;

export const ServiceModelUpdateInputSchema = z.object({
  provider: z.string().min(1).optional(),
  model: z.string().min(1),
  expected_revision: z.number().int().nonnegative().optional(),
}).strict();

export const ServiceRoutingRevisionSchema = z.object({
  service_key: z.string(),
  governance_revision: z.coerce.number().int().nonnegative(),
});
